import isEqual from 'lodash/isEqual';
import * as React from 'react';
import { useForm, useFormState } from 'react-hook-form';

import type { CoverageParams, CoveragePremiumValue, CoverageValues, PNCShopEventHandlers } from './Shop';
import type { MoneyField } from '@insify/global-money';
import type { SubmitErrorHandler, SubmitHandler, UnpackNestedValue, UseFormReturn } from 'react-hook-form';

type OnSelect<CoverageType extends string> = (coverage: { type: CoverageType; amount: MoneyField }) => void;
type OnToggle<CoverageType extends string> = (coverage: { type: CoverageType; enabled: boolean }) => void;
type OnPropertyChange<PropertyMultiRiskType extends string> = (coverage: {
  type: PropertyMultiRiskType;
  amount: MoneyField;
}) => void;
export type OnSubmit<CoverageType extends string, PropertyMultiRiskType extends string> = SubmitHandler<
  CoverageParams<CoverageType, PropertyMultiRiskType>
>;
export type OnError<CoverageType extends string, PropertyMultiRiskType extends string> = SubmitErrorHandler<
  CoverageValues<CoverageType, PropertyMultiRiskType>['params']
>;

type UseCoverageFormState<CoverageType extends string = string, PropertyMultiRiskType extends string = string> = {
  coveragesFormMethods: UseFormReturn<Record<string, CoverageType>>;
  onSelect: OnSelect<CoverageType>;
  onToggle: OnToggle<CoverageType>;
  onPropertyChange: OnPropertyChange<PropertyMultiRiskType>;
};

type Props<CoverageType extends string, PropertyMultiRiskType extends string> = Pick<
  CoverageValues<CoverageType, PropertyMultiRiskType>,
  'params' | 'defaultValues'
> & {
  eventHandlers: PNCShopEventHandlers<CoverageType, PropertyMultiRiskType>['eventHandlers'] & {
    onValidityChange: (isValid: boolean) => void;
  };
  children: React.ReactNode;
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const useCoverageFormContext = React.createContext<UseCoverageFormState<any, any> | undefined>(undefined);
const useCoveragePremiumsContext = React.createContext<
  { premium: CoveragePremiumValue; isLoading: boolean } | undefined
>(undefined);

function UseCoverageFormProvider<CoverageType extends string, PropertyMultiRiskType extends string>({
  children,
  eventHandlers,
  params,
  defaultValues,
}: Props<CoverageType, PropertyMultiRiskType>): JSX.Element {
  const coveragesFormMethods = useForm<CoverageValues<CoverageType, PropertyMultiRiskType>['params']>({
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    defaultValues: { ...defaultValues, ...params } as any,
    mode: 'onChange',
  });
  const [previousParams, setPreviousParams] = React.useState(params);

  const { isValid } = useFormState({ control: coveragesFormMethods.control });

  React.useEffect(() => {
    eventHandlers.onValidityChange?.(isValid);
  }, [isValid]);

  const onSelect: OnSelect<CoverageType> = ({ type, amount }) => {
    type === 'deductible'
      ? eventHandlers.onSelectDeductible({ amount })
      : eventHandlers.onSelect({ type, coverLimit: amount });
  };

  const onToggle: OnToggle<CoverageType> = (event) => {
    eventHandlers.onToggle?.(event);
  };

  const onPropertyChange: OnPropertyChange<PropertyMultiRiskType> = (event) => {
    eventHandlers.onPropertyChange?.(event);
  };

  const onSubmit: OnSubmit<CoverageType, PropertyMultiRiskType> = (formParams) => {
    eventHandlers.onSubmit(formParams);
  };

  const onError: OnError<CoverageType, PropertyMultiRiskType> = (formErrors) => {
    eventHandlers.onError(formErrors);
  };

  React.useEffect(() => {
    const paramsHaveChanged = !isEqual(params, previousParams);
    setPreviousParams(params);
    if (paramsHaveChanged) {
      coveragesFormMethods.reset(params as UnpackNestedValue<CoverageParams<CoverageType, PropertyMultiRiskType>>);
    }
  }, [params]);

  return (
    <useCoverageFormContext.Provider value={{ coveragesFormMethods, onSelect, onToggle, onPropertyChange }}>
      <form onSubmit={coveragesFormMethods.handleSubmit(onSubmit, onError)}>{children}</form>
    </useCoverageFormContext.Provider>
  );
}

function TotalPremiumProvider({
  premium,
  isLoading,
  children,
}: React.PropsWithChildren<{ premium: CoveragePremiumValue; isLoading: boolean }>) {
  return (
    <useCoveragePremiumsContext.Provider value={{ premium, isLoading }}>{children}</useCoveragePremiumsContext.Provider>
  );
}

function useCoveragesForm() {
  const context = React.useContext(useCoverageFormContext);
  if (context === undefined) {
    throw new Error('A CoverageInput must be used within a Coverages page');
  }
  return context;
}

function useCoveragesPremiums() {
  const context = React.useContext(useCoveragePremiumsContext);
  if (context === undefined) {
    throw new Error('A useCoveragesPremiums must be used within a Coverages page');
  }
  return context;
}

function usePropertyCoverage<PropertyName>(propertyName: PropertyName) {
  const { onPropertyChange: propertyChangeHandler, coveragesFormMethods } = useCoveragesForm();
  const { isLoading } = useCoveragesPremiums();

  const onPropertyChange = (amount: MoneyField) => propertyChangeHandler({ type: propertyName, amount });

  /** This part has to be refined depending on how\where we want to get the individual property params/premiums from. */
  const premium = (coveragesFormMethods.getValues().params?.propertyMultiRisk?.[propertyName]
    ?.premium as MoneyField) || { cents: '0', currency: 'EUR' };

  const sumInsured = (coveragesFormMethods.getValues()?.propertyMultiRisk?.[propertyName] as MoneyField) || {
    cents: '0',
    currency: 'EUR',
  };

  return { onPropertyChange, premium, sumInsured, isLoading };
}

export { UseCoverageFormProvider, useCoveragesForm, useCoveragesPremiums, usePropertyCoverage, TotalPremiumProvider };
